import { useEffect, useState, FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { formatApiError } from '@/services/api';
import { isValidEmail } from '@/utils/validation';
import { magicLoginService } from '@/services/magic-login.service';
import { useAuth } from '@/hooks/useAuth';
import { useAuthMethodsState } from '@/hooks/useAuthMethods';

const EMAIL_CONFIRMATION_CODE = 'magic.email_confirmation_required';
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** Single-use handoff key written by the inline script at the top of index.html. */
const TOKEN_STORAGE_KEY = 'aa_magic_token';

function takeStoredToken(): string | null {
  try {
    const value = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Landing page for the emailed login link (/auth/magic#token=...).
 * The token lives in the URL fragment (never sent to a server) and is only consumed by an
 * explicit click that POSTs it, so link scanners / prefetchers that merely open the URL
 * (even ones that run JavaScript) cannot use up the login.
 */
export const MagicLoginLanding = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { loginWithResponse } = useAuth();
  const { methods, isLoading: methodsLoading } = useAuthMethodsState();
  const [token, setToken] = useState<string | null>(null);
  // Same default as the password login: off.
  const [rememberMe, setRememberMe] = useState(false);
  // Set when the server says this browser did not request the link: the user must type the account email.
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    // Normally index.html's first-party script already moved the token into sessionStorage and
    // stripped the hash before any third-party script ran; the hash is only a fallback.
    const match = /(?:^#|&)token=([^&]+)/.exec(window.location.hash);
    const candidate = takeStoredToken() ?? (match ? match[1] : null);
    if (candidate && TOKEN_PATTERN.test(candidate)) {
      setToken(candidate);
    }
    // Drop the secret from the address bar / history right away.
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const verifyMutation = useMutation({
    mutationFn: (vars: { linkToken: string; email?: string }) =>
      magicLoginService.verifyLink(vars.linkToken, rememberMe, vars.email),
    onSuccess: async (response) => {
      await loginWithResponse(response.accessToken, response.user);
      navigate('/', { replace: true });
    },
    onError: (err: unknown, vars) => {
      if (formatApiError(err).code === EMAIL_CONFIRMATION_CODE) {
        setNeedsEmail(true);
        setEmailError('');
      } else if (vars.email) {
        // Wrong address (or a locked / expired link): one generic message, the form stays.
        setEmailError(t('auth_magic_confirm_email_mismatch'));
      }
    },
  });

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!isValidEmail(email.trim())) {
      setEmailError(t('auth_login_error_invalid_email'));
      return;
    }
    setEmailError('');
    verifyMutation.mutate({ linkToken: token, email: email.trim() });
  };

  if (!methodsLoading && !methods.magicLogin) {
    return <Navigate to="/login" replace />;
  }

  const failed = token === null || (verifyMutation.isError && !needsEmail);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      <div className="relative bg-white rounded-lg shadow-2xl px-7 py-8 w-full max-w-lg text-center">
        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
          {t('auth_magic_landing_heading')}
        </h1>
        {failed ? (
          <>
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" role="alert">
              {t('auth_magic_landing_error')}
            </div>
            <Link to="/login" className="text-bs-secondary hover:text-secondary-600 font-semibold text-sm">
              {t('auth_magic_landing_back')}
            </Link>
          </>
        ) : (
          needsEmail ? (
            <form onSubmit={handleConfirm} className="space-y-4 text-left">
              <p className="text-sm text-gray-600 text-center">{t('auth_magic_confirm_email_body')}</p>
              {emailError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" role="alert">
                  {emailError}
                </div>
              )}
              <Input
                inputSize="sm"
                labelVisible
                id="magic-confirm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth_login_placeholder_email')}
                disabled={verifyMutation.isPending}
                autoComplete="email"
                required
                label={t('auth_magic_confirm_email_label')}
              />
              <div className="flex items-center">
                <Toggle label={t('auth_login_remember_me')} checked={rememberMe} onChange={setRememberMe} />
              </div>
              <div className="text-center">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={verifyMutation.isPending}
                  className="uppercase tracking-wide"
                >
                  {t('auth_magic_landing_button')}
                </Button>
              </div>
              <div className="text-center">
                <Link to="/login" className="text-bs-secondary hover:text-secondary-600 font-semibold text-sm">
                  {t('auth_magic_landing_back')}
                </Link>
              </div>
            </form>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-5">
                {verifyMutation.isPending ? t('auth_magic_landing_working') : t('auth_magic_landing_body')}
              </p>
              <div className="flex items-center justify-center mb-5">
                <Toggle label={t('auth_login_remember_me')} checked={rememberMe} onChange={setRememberMe} />
              </div>
              <Button
                variant="primary"
                size="lg"
                loading={verifyMutation.isPending}
                disabled={methodsLoading}
                onClick={() => token && verifyMutation.mutate({ linkToken: token })}
                className="uppercase tracking-wide"
              >
                {t('auth_magic_landing_button')}
              </Button>
            </>
          )
        )}
      </div>
    </div>
  );
};
